import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Grid, 
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useNavigate } from 'react-router-dom';
import { Save as SaveIcon } from '@mui/icons-material';
import FirebaseService from '../services/FirebaseService';

const AddAccreditedOrganization = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    org_name: '',
    certi_no: '',
    city: '',
    state: '',
    country: '',
    program: '',
    status: 'ACCREDITED',
    exp_date: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      exp_date: date
    });
  };

  const validateForm = () => {
    const requiredFields = ['org_name', 'certi_no', 'city', 'state', 'country', 'program', 'status', 'exp_date'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Format the date to ISO string format (YYYY-MM-DD)
      const formattedData = {
        ...formData,
        exp_date: formData.exp_date ? formData.exp_date.toISOString().split('T')[0] : null
      };
      
      await FirebaseService.addAccreditedOrganization(formattedData);
      setSuccess(true);
      
      // Reset form after successful submission
      setFormData({
        org_name: '',
        certi_no: '',
        city: '',
        state: '',
        country: '',
        program: '',
        status: 'ACCREDITED',
        exp_date: null
      });
      
      // Navigate back to the organizations list after a short delay
      setTimeout(() => {
        navigate('/admin/accredited-organizations');
      }, 2000);
      
    } catch (err) {
      // Removed console.error
      setError('Failed to add organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" href="/">
            Home
          </Link>
          <Link 
            underline="hover" 
            color="inherit" 
            href="/admin/accredited-organizations"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/accredited-organizations');
            }}
          >
            Accredited Organizations
          </Link>
          <Typography color="text.primary">Add New Organization</Typography>
        </Breadcrumbs>
        
        <Typography variant="h4" component="h1" gutterBottom>
          Add New Accredited Organization
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Organization added successfully! Redirecting to organizations list...
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Organization Details
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Organization Name"
                name="org_name"
                value={formData.org_name}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Certificate Number"
                name="certi_no"
                value={formData.certi_no}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="State/Province"
                name="state"
                value={formData.state}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Program"
                name="program"
                value={formData.program}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g. ISO/IEC 17024-1:2012"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="ACCREDITED">ACCREDITED</MenuItem>
                  <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                  <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expiry Date"
                  value={formData.exp_date}
                  onChange={handleDateChange}
                  renderInput={(params) => 
                    <TextField 
                      {...params} 
                      fullWidth 
                      required 
                      name="exp_date"
                    />
                  }
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/accredited-organizations')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Organization'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default AddAccreditedOrganization;
